import { NotFoundError, SessionExpiredError, UnauthorizedError } from '@alice-financial/api'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as React from 'react'
import { IntlConfig } from 'react-intl'
import { LocalizationProvider, SupportedLangCode } from './i18n'
import { GlobalNotificationProvider } from './notifications/GlobalNotification'
import { AnalyticsProvider } from './telemetry/AnalyticsProvider'
import { TrackingContextProvider } from './telemetry/TrackingProvider'

// Our fetcher will throw custom errors for unrecoverable errors - these are errors
// that won't be fixed by retrying the request
const errorIsUnrecoverable = (error: unknown) =>
  error instanceof UnauthorizedError || error instanceof NotFoundError || error instanceof SessionExpiredError

const DATA_STALE_TIME = 1000 * 60 * 10 // 10 minutes
const DEFAULT_RETRIES = 2

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: DATA_STALE_TIME, // 10 minute lazy refetch by default
      retry: (failureCount, error) => !errorIsUnrecoverable(error) && failureCount < DEFAULT_RETRIES,
    },
  },
})

type AliceSDKProviderProps = {
  children: React.ReactNode
  messageMap: Record<SupportedLangCode, Record<string, string>>
  defaultRichTextElements?: IntlConfig['defaultRichTextElements']
}
/**
 * Wrap the application with this component in order for nested components to use
 * React Query for API calls
 */
export const AliceSDKProvider = ({
  children,
  messageMap,
  defaultRichTextElements,
}: AliceSDKProviderProps) => (
  <QueryClientProvider client={queryClient}>
    <AnalyticsProvider>
      <TrackingContextProvider>
        <LocalizationProvider messageMap={messageMap} defaultRichTextElements={defaultRichTextElements}>
          <GlobalNotificationProvider>{children}</GlobalNotificationProvider>
        </LocalizationProvider>
      </TrackingContextProvider>
    </AnalyticsProvider>
  </QueryClientProvider>
)
